import { Switch as MuiSwitch, SwitchProps as MuiSwitchProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';

export interface SwitchProps extends Omit<MuiSwitchProps, 'size'> {
  /**
   * 预设尺寸大小
   */
  size?: 'small' | 'medium';
  /**
   * 自定义轨道宽度（单位：px，优先级高于size）
   */
  width?: number;
  /**
   * 自定义轨道高度（单位：px，优先级高于size）
   * thumbSize会根据height自动计算 (thumbSize = height - 4)
   */
  height?: number;
}

/**
 * AntSwitch - 完全按照Material-UI官网示例实现
 * 参考: https://mui.com/material-ui/react-switch/#customization
 */
const AntSwitch = styled(MuiSwitch, {
  shouldForwardProp: (prop) =>
    prop !== 'switchSize' && prop !== 'customWidth' && prop !== 'customHeight',
})<{
  switchSize?: 'small' | 'medium';
  customWidth?: number;
  customHeight?: number;
}>(({ theme, switchSize = 'small', customWidth, customHeight }) => {
  // 预设尺寸配置
  const presetSizeConfig = {
    small: {
      width: 28,
      height: 16,
      thumbSize: 12,
    },
    medium: {
      width: 42,
      height: 22,
      thumbSize: 18,
    },
  };

  // 获取最终配置：自定义尺寸优先，回退到预设尺寸
  const finalWidth = customWidth ?? presetSizeConfig[switchSize].width;
  const finalHeight = customHeight ?? presetSizeConfig[switchSize].height;

  // 自动计算thumbSize：比height小4px，确保有合适的视觉边距
  // 最小thumbSize为6px，避免过小导致的视觉问题
  const calculateThumbSize = finalHeight - 4;
  const finalThumbSize = Math.max(calculateThumbSize, 6);

  // 自动计算translateDistance，确保左右边距都是2px
  // 公式: trackWidth - thumbSize - 4px (左右各2px边距)
  const calculateTranslateDistance = finalWidth - finalThumbSize - 4;

  // 边界保护：确保translateDistance不为负数
  const translateDistance = Math.max(calculateTranslateDistance, 0);

  const config = {
    width: finalWidth,
    height: finalHeight,
    thumbSize: finalThumbSize,
    translateDistance,
  };

  return {
    width: config.width,
    height: config.height,
    padding: 0,
    display: 'flex',
    '&:active': {
      '& .MuiSwitch-thumb': {
        width: config.thumbSize + 2,
        transition: theme.transitions.create(['width'], {
          duration: theme.transitions.duration.shortest,
        }),
      },
      '& .MuiSwitch-switchBase.Mui-checked': {
        transform: `translateX(${config.translateDistance - 1}px)`,
      },
    },
    '& .MuiSwitch-switchBase': {
      padding: 2,
      color: theme.palette.grey?.[500] || '#999',
      transition: theme.transitions.create(['transform', 'color'], {
        duration: theme.transitions.duration.shorter,
      }),
      '&.Mui-checked': {
        transform: `translateX(${config.translateDistance}px)`,
        color: theme.palette.common.white,
        '& + .MuiSwitch-track': {
          opacity: 1,
          backgroundColor: '#463AE8', // 自定义紫色
          borderColor: '#463AE8',
        },
      },
    },
    '& .MuiSwitch-thumb': {
      width: config.thumbSize,
      height: config.thumbSize,
      backgroundColor: '#ffffff', // 明确设置thumb为白色
      boxShadow: 'none',
      transition: theme.transitions.create(['width'], {
        duration: theme.transitions.duration.shorter,
      }),
    },
    '& .MuiSwitch-track': {
      border: `1px solid rgba(0, 0, 0, 0.1)`, // 使用您要求的边框色
      borderRadius: config.height / 2,
      opacity: 1,
      backgroundColor: '#EBEBEF', // 使用您要求的背景色
      boxSizing: 'border-box',
      transition: theme.transitions.create(['background-color', 'border-color'], {
        duration: theme.transitions.duration.shorter,
      }),
    },
  };
});

/**
 * 自定义Switch开关组件
 * 完全基于Material-UI Switch + styled自定义样式
 * 支持预设尺寸和自定义尺寸
 *
 * thumbSize自动计算规则：
 * - thumbSize = height - 4px (确保有2px的上下边距)
 * - 最小thumbSize = 6px (避免过小影响视觉效果)
 * - translateDistance自动计算确保左右边距各2px
 *
 * @example
 * // 使用预设尺寸
 * <Switch size="small" />    // 28×16px, thumbSize=12px
 * <Switch size="medium" />   // 42×22px, thumbSize=18px
 *
 * // 使用自定义尺寸（thumbSize自动计算）
 * <Switch width={50} height={30} />  // thumbSize=26px
 *
 * // 混合使用（自定义尺寸优先）
 * <Switch size="medium" width={50} />  // width=50, height=22, thumbSize=18
 */
const Switch: React.FC<SwitchProps> = ({ size = 'small', width, height, onChange, ...props }) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event, event.target.checked);
    }
  };

  return (
    <AntSwitch
      switchSize={size}
      customWidth={width}
      customHeight={height}
      onChange={handleChange}
      disableRipple
      {...props}
    />
  );
};

export default Switch;
